/**
 * **Settings › Parsing** (engine W12, FR-E38, plan D19) — what the Settings
 * screen needs of the service for the engine's org settings, beyond the
 * package's client.
 *
 * The `parsing` section rides the settings document the package already reads
 * and PATCHes (`offered`, `profiles_off`, `auto_accept`); two routes are new
 * and are the app's own (contract C-E3, `docs/sessions/2026-10-13-w12-session-prompts.md`):
 *
 *     GET /api/v1/profiles                        → the org's view of the profiles,
 *                                                   `meta.ai_available`
 *     PUT /api/v1/settings/parsing/ai-processing  { ai_processing, version }  — step-up
 *
 * The first is readable by any member admitted to ProDoc; a switched-off
 * profile is listed `enabled: false`, an internal one `internal: true`.
 * `ai_processing` changes **only** through the second, behind
 * `settings:can_update` and step-up exactly as a retention override is (D13),
 * naming the settings version it read — a stale one is `409`, as the PATCH's
 * is; the general PATCH refuses it. An `internal` profile (`_smoke`, C-E1) is
 * the plumbing's proof and is never offered here, whatever the service lists.
 *
 * **Engine W16 adds the month's usage** (T16.9, FR-E36, D43; contract C-E11,
 * `docs/sessions/2026-10-15-w16-session-prompts.md`):
 *
 *     GET /api/v1/settings/parsing/usage?month=YYYY-MM   — `settings:can_update`
 *
 * the month's parses (completed, refused, failed), the calls to an AI model and
 * their cost in **integer paise**, by profile — counts and paise only, never a
 * document; the organisation's administrator's (FR-E36). A job counts in the
 * IST month it ended; the plumbing's own profiles are left out. Without
 * `month` the service answers the current month in IST, and says which in
 * `month`; the screen takes the current month from that answer, never from
 * the browser's clock.
 *
 * The package carries the four capabilities to a sibling (ADR-0010); org
 * settings are this app's screen, so these calls stay in the app.
 */

import { ApiError, PROFILE_FOR_TYPE, STEPUP_HEADER, type DocumentOptions, type ProDocClientOptions, type SettingsAnswer } from '@fiveox/prodoc-ui'

export type AiProcessing = 'external' | 'india_only'

/** The `parsing` section of the settings document (D19). */
export interface ParsingSettings {
  /** Whether ProDoc's own screens offer a parse (FR-E3). An app's request does not read it. */
  offered: boolean
  /** Profiles the organisation switched off — refused to everyone, person or app (FR-E4). */
  profiles_off: string[]
  /** FR-E31: `external` may reach a model on Google Cloud; `india_only` never reaches an external adapter. */
  ai_processing: AiProcessing
  /** FR-E19: an `auto` extraction that reconciles is accepted without a reviewer. */
  auto_accept: boolean
}

/** The shipped defaults (D19) — what the screen assumes where the service is silent. */
export const PARSING_DEFAULTS: ParsingSettings = { offered: true, profiles_off: [], ai_processing: 'external', auto_accept: true }

/** One profile as `GET /api/v1/profiles` answers it (C-E3) — the org's view, switched-off ones `enabled: false`. */
export interface ParseProfile {
  /** The document-type code it reads (`invoice`, `form16`, …). */
  name: string
  schema_version: number | null
  mode: string | null
  media_types: string[]
  options: Record<string, unknown>
  enabled: boolean
  /** C-E1's flag, when the answer carries it. */
  internal?: boolean
}

const asRecord = (v: unknown): Record<string, unknown> | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null)

/** The section in force, the shipped default for anything absent. */
export function parsingOf(settings: SettingsAnswer | null | undefined): ParsingSettings {
  const p = asRecord(settings?.effective?.parsing) ?? {}
  const ai = p.ai_processing
  return {
    offered: typeof p.offered === 'boolean' ? p.offered : PARSING_DEFAULTS.offered,
    profiles_off: Array.isArray(p.profiles_off) ? p.profiles_off.filter((n): n is string => typeof n === 'string') : [],
    ai_processing: ai === 'external' || ai === 'india_only' ? ai : PARSING_DEFAULTS.ai_processing,
    auto_accept: typeof p.auto_accept === 'boolean' ? p.auto_accept : PARSING_DEFAULTS.auto_accept,
  }
}

/**
 * The plumbing's own profiles never reach a person: C-E1's `internal` flag
 * when the answer carries it, and the leading underscore that names them
 * (`_smoke`) when it does not — the service lists them in `local`.
 */
export const isInternalProfile = (p: { name: string; internal?: boolean }): boolean => p.internal === true || p.name.startsWith('_')

/**
 * The profiles a person sees and switches: the service's list without the
 * internal ones — and, as a defence, every profile the organisation switched
 * off that the list does not carry (C-E3 lists them `enabled: false`), so one
 * switched off can always be switched back on.
 */
export function readableProfiles(listed: ParseProfile[] | null | undefined, profilesOff: string[]): ParseProfile[] {
  const shown = (listed ?? []).filter((p) => !isInternalProfile(p))
  const named = new Set((listed ?? []).map((p) => p.name))
  for (const name of profilesOff) {
    if (named.has(name) || isInternalProfile({ name })) continue
    named.add(name)
    shown.push({ name, schema_version: null, mode: null, media_types: [], options: {}, enabled: false })
  }
  return shown
}

export function profilesOf(body: unknown): ParseProfile[] {
  const record = asRecord(body)
  const raw = Array.isArray(body) ? body : Array.isArray(record?.profiles) ? (record!.profiles as unknown[]) : []
  const out: ParseProfile[] = []
  for (const entry of raw) {
    const r = asRecord(entry)
    if (!r || typeof r.name !== 'string' || !r.name) continue
    out.push({
      name: r.name,
      schema_version: typeof r.schema_version === 'number' ? r.schema_version : null,
      mode: typeof r.mode === 'string' ? r.mode : null,
      media_types: Array.isArray(r.media_types) ? r.media_types.filter((m): m is string => typeof m === 'string') : [],
      options: asRecord(r.options) ?? {},
      enabled: r.enabled !== false,
      ...(typeof r.internal === 'boolean' ? { internal: r.internal } : {}),
    })
  }
  return out
}

const isSettingsAnswer = (v: unknown): v is SettingsAnswer => {
  const r = asRecord(v)
  return Boolean(r && asRecord(r.effective) && typeof r.version === 'number')
}

/** `GET /api/v1/profiles`: the list, and whether AI is switched on for ProDoc at all. */
export interface ProfileList {
  items: ParseProfile[]
  /**
   * `meta.ai_available`: `false` while no AI adapter is switched on (FR-E34) —
   * a document that needs AI then goes to a reviewer whatever the org chose.
   * `null` when the answer does not say.
   */
  ai_available: boolean | null
  /**
   * W19 (C-E26, FR-E70): `meta.inhouse_available` — ProDoc's own AI model, in India, is
   * configured here, so *India only* means "read by ProDoc's own model" rather than "goes to
   * a reviewer". `null` when the answer does not say (an older service): read as `false`.
   */
  inhouse_available: boolean | null
}

/* ------------------------------------------------- the month's usage (C-E11) */

/** A month as the service names it: `2026-10`. */
export const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/

/** One profile's month (C-E11's `by_profile`). Money is integer paise. */
export interface ProfileUsage {
  profile: string
  completed: number
  refused: number
  model_calls: number
  ai_cost_paise: number
  /**
   * Engine W32 (FR-E53): the profile's completed structured or mapped readings that called
   * no model, and so used no unit. Present only when the service sent it (W32 on).
   */
  read_without_model?: number
}

/** `GET /api/v1/settings/parsing/usage` (C-E11): an organisation's month of parsing — counts and paise only. */
export interface ParsingUsage {
  /** `YYYY-MM` — the month the service counted; `null` when the answer does not say. */
  month: string | null
  /** W32 (FR-E53): `read_without_model` — completed readings that called no model, so no unit; present only when the service sent it. */
  parses: { completed: number; refused: number; failed: number; read_without_model?: number }
  model_calls: number
  ai_cost_paise: number
  by_profile: ProfileUsage[]
}

/** A count or an amount in paise as the service sent it — a non-negative whole number, anything else none. */
const whole = (v: unknown): number => (typeof v === 'number' && Number.isSafeInteger(v) && v >= 0 ? v : 0)

/** The usage answer, read field by field: what is missing or not a whole number counts as none. */
export function usageOf(body: unknown, asked: string | null = null): ParsingUsage {
  const r = asRecord(body) ?? {}
  const parses = asRecord(r.parses) ?? {}
  const month = typeof r.month === 'string' && MONTH.test(r.month) ? r.month : asked
  const rows: ProfileUsage[] = []
  for (const entry of Array.isArray(r.by_profile) ? r.by_profile : []) {
    const p = asRecord(entry)
    if (!p || typeof p.profile !== 'string' || !p.profile) continue
    rows.push({
      profile: p.profile,
      completed: whole(p.completed),
      refused: whole(p.refused),
      model_calls: whole(p.model_calls),
      ai_cost_paise: whole(p.ai_cost_paise),
      // W32 (FR-E53): said only by a service that counts it; a value that is not a whole number counts as none.
      ...('read_without_model' in p ? { read_without_model: whole(p.read_without_model) } : {}),
    })
  }
  return {
    month,
    parses: {
      completed: whole(parses.completed),
      refused: whole(parses.refused),
      failed: whole(parses.failed),
      ...('read_without_model' in parses ? { read_without_model: whole(parses.read_without_model) } : {}),
    },
    model_calls: whole(r.model_calls),
    ai_cost_paise: whole(r.ai_cost_paise),
    by_profile: rows,
  }
}

/** Whether the month read nothing at all — no parse ended and no model was called. */
export const isIdleMonth = (u: ParsingUsage): boolean => u.parses.completed + u.parses.refused + u.parses.failed + u.model_calls === 0 && u.by_profile.length === 0

/** The `count` months ending at `latest`, newest first — what the screen offers to pick. */
export function recentMonths(latest: string, count = 12): string[] {
  if (!MONTH.test(latest)) return []
  let year = Number(latest.slice(0, 4))
  let month = Number(latest.slice(5, 7))
  const out: string[] = []
  for (let i = 0; i < count; i += 1) {
    out.push(`${year}-${String(month).padStart(2, '0')}`)
    month -= 1
    if (month === 0) {
      month = 12
      year -= 1
    }
  }
  return out
}

export interface ParsingApi {
  /** The org's profiles (C-E3), the internal ones included as the service lists them — the screen drops those. */
  profiles: () => Promise<ProfileList>
  /**
   * The one way `ai_processing` changes: `settings:can_update` **and** the
   * assertion of a step-up minted for this request, naming the settings
   * `version` read — a stale one is `CONFLICT` (`version_conflict`), as the
   * PATCH's is. The settings document as the service answers it, or `null`
   * when the answer is not one (the screen then reads the settings again).
   */
  setAiProcessing: (input: { ai_processing: AiProcessing; version: number }, opts?: { stepUpAssertion?: string }) => Promise<SettingsAnswer | null>
  /**
   * C-E11: the month's usage, `settings:can_update` — `month` as `YYYY-MM`, or
   * omitted for the current month in IST (the answer's `month` says which).
   */
  usage: (month?: string | null) => Promise<ParsingUsage>
}

const ORG_HEADER = 'X-Fiveox-Org'

/** The two routes over the host's fetcher, with the org header and the service's envelope — as `createProDocClient` speaks them. */
export function createParsingApi({ baseUrl, fetcher, orgId }: ProDocClientOptions): ParsingApi {
  const base = baseUrl.replace(/\/+$/, '')

  async function call(path: string, init: { method?: string; body?: unknown; stepUpAssertion?: string } = {}): Promise<{ data: unknown; meta: Record<string, unknown> }> {
    const org = typeof orgId === 'function' ? orgId() : orgId
    if (!org) throw new ApiError('VALIDATION_ERROR', 'No organisation is selected.', 400)
    // A plain object: the shell's `authed` spreads it, and a `Headers` would vanish.
    const headers: Record<string, string> = { accept: 'application/json', [ORG_HEADER]: org }
    if (init.body !== undefined) headers['content-type'] = 'application/json'
    // Only on the one request it was minted for.
    if (init.stepUpAssertion) headers[STEPUP_HEADER] = init.stepUpAssertion
    let res: Response
    try {
      res = await fetcher(`${base}/api/v1${path}`, { method: init.method ?? 'GET', headers, body: init.body === undefined ? undefined : JSON.stringify(init.body) })
    } catch {
      throw new ApiError('INTERNAL', 'Could not reach ProDoc.', 0)
    }
    const payload = (await res.json().catch(() => null)) as {
      success?: boolean
      data?: unknown
      meta?: Record<string, unknown>
      error?: { code?: string; message?: string; details?: Record<string, unknown> }
    } | null
    if (!res.ok || !payload?.success) {
      throw new ApiError(payload?.error?.code ?? 'INTERNAL', payload?.error?.message ?? 'Something went wrong.', res.status, payload?.error?.details ?? {})
    }
    return { data: payload.data, meta: asRecord(payload.meta) ?? {} }
  }

  return {
    profiles: async () => {
      const { data, meta } = await call('/profiles')
      return {
        items: profilesOf(data),
        ai_available: typeof meta.ai_available === 'boolean' ? meta.ai_available : null,
        inhouse_available: typeof meta.inhouse_available === 'boolean' ? meta.inhouse_available : null,
      }
    },
    setAiProcessing: async (input, opts = {}) => {
      const { data } = await call('/settings/parsing/ai-processing', {
        method: 'PUT',
        body: { ai_processing: input.ai_processing, version: input.version },
        stepUpAssertion: opts.stepUpAssertion,
      })
      return isSettingsAnswer(data) ? data : null
    },
    usage: async (month = null) => {
      if (month !== null && !MONTH.test(month)) throw new ApiError('VALIDATION_ERROR', 'A month is written YYYY-MM.', 400, { field: 'month' })
      const { data } = await call(`/settings/parsing/usage${month ? `?month=${month}` : ''}`)
      return usageOf(data, month)
    },
  }
}

const MEDIA: Record<string, string> = {
  'application/pdf': 'PDF',
  'text/csv': 'CSV',
  'application/json': 'JSON',
  'text/plain': 'Text',
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
  'image/tiff': 'TIFF',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'application/vnd.ms-excel': 'Excel',
}

/** "PDF, CSV" — the files a profile reads, said the way a person names them. */
export function mediaText(types: string[]): string {
  return [...new Set(types.map((t) => MEDIA[t] ?? (t.split('/')[1] ?? t).toUpperCase()))].join(', ')
}

/**
 * Engine W17 (D54): the other document types a profile reads, by the package's type →
 * profile table — `invoice` reads credit and debit notes — so switching it off is seen to
 * stop those too. `[]` for a profile that reads only its own type.
 */
export function alsoReads(profile: string): string[] {
  return [...PROFILE_FOR_TYPE].filter(([type, read]) => read === profile && type !== profile).map(([type]) => type)
}

/** A document type's label from a list of them, else its code said plainly. */
export const typeLabel = (types: { code: string; label: string }[], code: string) => types.find((t) => t.code === code)?.label ?? code.replace(/_/g, ' ')

/**
 * A profile is named for the document type it reads: the enabled types'
 * labels (`/documents/options`) first, then every type the settings hold —
 * the Parsing page's switches and the usage table say the same name.
 */
export function profileLabelOf(options: Pick<DocumentOptions, 'document_types'> | null | undefined, settings: SettingsAnswer | null | undefined): (name: string) => string {
  const types = [...(options?.document_types ?? []), ...(settings?.effective.document_types ?? [])]
  return (name) => typeLabel(types, name)
}
